import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, FileUp, Upload } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getFideImportProgress, importFideList } from '@/lib/api';
import { ApiError } from '@/lib/api/core';
import { showErrorToast, showSuccessToast } from '@/lib/toast';

const POLL_INTERVAL_MS = 2000;
// Poll cap — the full list takes minutes; if the job is somehow still
// "running" after this long, stop polling and let the user re-check status.
const MAX_POLLS = 900; // 30 min

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

// Story 098 — admin uploads FIDE's monthly rating-list dump
// (players_list_xml.zip or the extracted .xml) to refresh the local mirror
// that powers FIDE-ID autofill. Thin wrapper over POST /admin/fide/import.
export function FideImportCard({ onImported }: { onImported?: () => void } = {}) {
  const { t } = useTranslation('director');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [progressCount, setProgressCount] = useState<number | null>(null);

  // Upload (long timeout — the file is ~70 MB), then poll the background
  // job's progress until it finishes. The ingest itself takes minutes for
  // the full list; a synchronous request would hit the client timeout and
  // read as a "connection error" while the server kept working.
  async function onImport() {
    if (!file) return;
    setImporting(true);
    setProgressCount(null);
    try {
      await importFideList(file);
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';

      for (let i = 0; i < MAX_POLLS; i++) {
        await sleep(POLL_INTERVAL_MS);
        const p = await getFideImportProgress();
        setProgressCount(p.imported);
        if (!p.running) {
          if (p.error) {
            showErrorToast(t('admin.fide_import.failed'));
          } else {
            showSuccessToast(
              t('admin.fide_import.success', { count: p.imported }),
            );
          }
          onImported?.();
          return;
        }
        // Keep the status card fresh while rows accumulate.
        if (i % 5 === 0) onImported?.();
      }
      // Poll cap hit — the job may still be running server-side.
      showErrorToast(t('admin.fide_import.still_running'));
      onImported?.();
    } catch (err) {
      showErrorToast(
        err instanceof ApiError ? err.userMessage : t('admin.fide_import.failed'),
      );
    } finally {
      setImporting(false);
      setProgressCount(null);
    }
  }

  return (
    <Card data-testid="fide-import-card">
      <CardContent className="space-y-3 py-6">
        <p className="text-sm text-muted-foreground">
          {t('admin.fide_import.description')}
        </p>
        {/* Where to get the file — FIDE's public download page. Direct-file
            link plus the page itself, since FIDE occasionally reshuffles the
            direct URL between list revisions. */}
        <p className="text-sm">
          <a
            href="https://ratings.fide.com/download_lists.phtml"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="fide-download-page-link"
            className="inline-flex items-center gap-1 text-primary underline underline-offset-2"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            {t('admin.fide_import.download_page_link')}
          </a>
          <span className="text-muted-foreground">
            {' '}
            {t('admin.fide_import.download_hint')}
          </span>
        </p>
        <div className="flex flex-wrap items-center gap-3">
          {/* Native file inputs look off-theme — hide the input and drive it
              from a styled button (same pattern as the dashboard TRF16
              import). Playwright's setInputFiles targets hidden inputs fine,
              so the e2e testid stays on the input. */}
          <input
            ref={inputRef}
            type="file"
            accept=".zip,.xml"
            data-testid="fide-import-input"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="hidden"
          />
          <Button
            variant="outline"
            data-testid="fide-import-choose-button"
            disabled={importing}
            onClick={() => inputRef.current?.click()}
          >
            <FileUp className="mr-2 h-4 w-4" />
            {t('admin.fide_import.choose_button')}
          </Button>
          <span
            data-testid="fide-import-filename"
            className="text-sm text-muted-foreground"
          >
            {file ? file.name : t('admin.fide_import.no_file_selected')}
          </span>
          <Button
            data-testid="fide-import-button"
            disabled={!file || importing}
            onClick={onImport}
          >
            <Upload className="mr-2 h-4 w-4" />
            {importing
              ? t('admin.fide_import.importing')
              : t('admin.fide_import.import_button')}
          </Button>
        </div>
        {importing && (
          <p
            data-testid="fide-import-progress"
            className="text-sm text-muted-foreground"
          >
            {progressCount != null
              ? t('admin.fide_import.progress', { count: progressCount })
              : t('admin.fide_import.uploading')}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
